import { Store } from "redux";
import { registerLocationUpdate } from "../redux/cartOperationsAdapter";
import { updateGroupLocation } from "../lib/updateGroupLocation";
import { setCart } from "../redux/cartReducer";
import CartOperationManager from "./CartOperationManager";
import { EventEmitter } from "events";

/**
 * Interface for cart group objects to fix type errors
 */
interface CartGroup {
  id: string;
  deliveryDate?: string;
  products: Array<any>;
  subTotal?: number;
  deliveryFee?: number;
  groupTotal?: number;
  totalWeight?: number;
  deliveryFeeZone?: {
    documentId: string;
  };
}

/**
 * Global service to monitor location changes and update the cart in the background
 * Provides a centralized way to handle cart location updates across the application
 */
class GlobalCartSyncService extends EventEmitter {
  private static instance: GlobalCartSyncService;
  private store: Store | null = null;
  private previousLocation: {
    deliveryFeeZoneId: string | null;
  } = {
    deliveryFeeZoneId: null,
  };
  private isInitialized = false;
  private unsubscribeFromStore: (() => void) | null = null;

  // Add property to track if a location update is already in progress through the operation manager
  private get isLocationUpdateInProgress(): boolean {
    return CartOperationManager.hasActiveOperations("LOCATION_UPDATE");
  }

  // Track the last update time to prevent duplicate calls
  private lastUpdateTime: number = 0;
  private readonly UPDATE_THROTTLE = 2000; // 2 seconds

  // Track pending location updates with IDs to avoid duplicates
  private pendingLocationUpdates: Set<string> = new Set();
  private lastDestinationZoneId: string | null = null;

  // Make constructor private for singleton pattern
  private constructor() {
    super();
  }

  // Get the singleton instance
  public static getInstance(): GlobalCartSyncService {
    if (!GlobalCartSyncService.instance) {
      GlobalCartSyncService.instance = new GlobalCartSyncService();
    }
    return GlobalCartSyncService.instance;
  }

  /**
   * Initialize the service with the Redux store
   */
  public initialize(store: Store): void {
    if (this.isInitialized) return;

    this.store = store;
    this.isInitialized = true;

    // Start monitoring location changes
    this.setupLocationMonitoring();

    console.log(
      "📍 GlobalCartSyncService initialized and monitoring location changes"
    );
  }

  /**
   * Clean up listeners when no longer needed
   */
  public cleanup(): void {
    if (this.unsubscribeFromStore) {
      this.unsubscribeFromStore();
      this.unsubscribeFromStore = null;
    }
    this.removeAllListeners();
  }

  /**
   * Public method to check if a location update is in progress
   */
  public isUpdateInProgress(): boolean {
    return this.isLocationUpdateInProgress;
  }

  /**
   * Public method to manually trigger a location update
   */
  public updateCartLocation(destinationZoneId: string): Promise<boolean> {
    // Add stack trace logging to identify caller
    const callerStack = new Error().stack;
    console.log("📍 updateCartLocation called from:", callerStack);

    // Check if this zone ID is already being processed
    if (this.pendingLocationUpdates.has(destinationZoneId)) {
      console.warn(
        `📍 Duplicate location update for zone ${destinationZoneId} detected, skipping`
      );
      return Promise.resolve(false);
    }

    // Skip if it's the same as the last destination we updated to
    if (this.lastDestinationZoneId === destinationZoneId) {
      console.warn(
        `📍 Location update to same zone ${destinationZoneId} requested, skipping`
      );
      return Promise.resolve(false);
    }

    if (this.isLocationUpdateInProgress) {
      console.warn(
        "📍 Location update already in progress, skipping duplicate call"
      );
      return Promise.resolve(false);
    }

    const now = Date.now();
    if (now - this.lastUpdateTime < this.UPDATE_THROTTLE) {
      console.warn(
        `📍 Location update throttled (${this.UPDATE_THROTTLE}ms between calls)`
      );
      return Promise.resolve(false);
    }

    this.lastUpdateTime = now;
    this.lastDestinationZoneId = destinationZoneId;
    this.pendingLocationUpdates.add(destinationZoneId);

    return this._updateCartWithNewLocation(destinationZoneId).finally(() => {
      this.pendingLocationUpdates.delete(destinationZoneId);
    });
  }

  /**
   * Public method to get current destination zone ID
   */
  public getCurrentDestinationZoneId(): string | null {
    return this.previousLocation.deliveryFeeZoneId;
  }

  /**
   * Subscribe to status updates
   * Returns an unsubscribe function
   */
  public onStatusChange(callback: (status: string) => void): () => void {
    this.on("status", callback);
    return () => this.off("status", callback);
  }

  /**
   * Set up subscription to monitor location changes in Redux
   */
  private setupLocationMonitoring(): void {
    if (!this.store) return;

    // Get initial location
    const state = this.store.getState();
    this.previousLocation = {
      deliveryFeeZoneId: state.location?.deliveryFeeZone?.documentId || null,
    };
    this.lastDestinationZoneId = this.previousLocation.deliveryFeeZoneId;

    console.log(
      "📍 Initial location zone:",
      this.previousLocation.deliveryFeeZoneId
    );

    // Use a debounced handler to prevent multiple rapid invocations
    let locationChangeTimeout: NodeJS.Timeout | null = null;

    // Subscribe to store changes to detect location updates
    this.unsubscribeFromStore = this.store.subscribe(() => {
      if (locationChangeTimeout) {
        clearTimeout(locationChangeTimeout);
      }

      // Debounce location change checks
      locationChangeTimeout = setTimeout(() => {
        this.checkForLocationChanges();
        locationChangeTimeout = null;
      }, 50);
    });

    // Do an immediate check - this helps with initialization
    this.checkForLocationChanges();
  }

  /**
   * Check if location has changed and trigger updates if needed
   */
  private checkForLocationChanges(): void {
    if (!this.store) return;

    if (this.isLocationUpdateInProgress) {
      console.log("📍 Skipping location check - update already in progress");
      return;
    }

    const now = Date.now();
    if (now - this.lastUpdateTime < this.UPDATE_THROTTLE) {
      console.log("📍 Skipping location check - throttled");
      return;
    }

    const currentState = this.store.getState();
    const currentZoneId =
      currentState.location?.deliveryFeeZone?.documentId || null;

    // Skip if zone ID is already in pending updates
    if (this.pendingLocationUpdates.has(currentZoneId)) {
      console.log(
        "📍 Skipping location check - already pending update for this zone"
      );
      return;
    }

    // Important check: if the current zone ID matches what we last updated to,
    // skip the update to prevent unnecessary duplicate calls
    if (
      this.lastDestinationZoneId === currentZoneId &&
      currentZoneId !== null
    ) {
      console.log("📍 Skipping location check - already updated to this zone");
      return;
    }

    if (
      currentZoneId !== this.previousLocation.deliveryFeeZoneId &&
      currentZoneId !== null
    ) {
      console.log(
        `📍 Location zone change detected: ${
          this.previousLocation.deliveryFeeZoneId || "none"
        } → ${currentZoneId}`
      );

      this.previousLocation.deliveryFeeZoneId = currentZoneId;
      this.lastUpdateTime = now;
      this.pendingLocationUpdates.add(currentZoneId);

      // Use small timeout to avoid race conditions with Redux updates
      setTimeout(() => {
        this._updateCartWithNewLocation(currentZoneId).finally(() => {
          this.pendingLocationUpdates.delete(currentZoneId);
        });
      }, 50);
    }
  }

  /**
   * Update cart groups with new location using CartOperationManager
   * Private implementation
   */
  private async _updateCartWithNewLocation(
    destinationZoneId: string
  ): Promise<boolean> {
    if (!this.store) return false;

    this.lastUpdateTime = Date.now();
    this.lastDestinationZoneId = destinationZoneId;

    console.log("📍 ====== BEGIN LOCATION UPDATE ======");
    console.log(`📍 Updating cart location to zone: ${destinationZoneId}`);

    const cartState = this.store.getState().cart;
    const dispatch = this.store.dispatch;

    const cartId = cartState?.cartId || localStorage.getItem("cartId");
    if (!cartId) {
      console.log("📍 No cartId found, skipping location update");
      return false;
    }

    try {
      this.emit("status", "updating");

      const groups: CartGroup[] = cartState?.groups || [];
      if (!groups.length) {
        console.log("📍 Cart has no groups, skipping location update");
        this.emit("status", "idle");
        return false;
      }

      const groupIds = groups.map((group) => group.id);

      console.log(
        `📍 Updating cart ${cartId} with new destination zone ${destinationZoneId} for ${groupIds.length} groups`
      );

      if (!this.isLocationUpdateInProgress) {
        const operation = registerLocationUpdate(
          dispatch,
          destinationZoneId,
          groupIds
        );

        try {
          const response = await updateGroupLocation(
            cartId,
            groupIds,
            destinationZoneId
          );

          if (response?.cart) {
            dispatch(setCart(response.cart));
            console.log(
              "📍 Background cart location update completed successfully"
            );
          } else {
            console.warn("📍 No cart data returned from updateGroupLocation");
          }

          operation.complete(true);
          this.emit("status", "complete");
          console.log("📍 ====== END LOCATION UPDATE ======");
          return true;
        } catch (error) {
          console.error("📍 Error updating cart location:", error);
          operation.complete(false);
          this.emit("status", "error");
          console.log("📍 ====== END LOCATION UPDATE (ERROR) ======");
          return false;
        }
      } else {
        console.warn(
          "📍 Location update already in progress via CartOperationManager, skipping"
        );
        console.log("📍 ====== END LOCATION UPDATE (SKIPPED) ======");
        return false;
      }
    } catch (error) {
      console.error("📍 Unexpected error during location update:", error);
      this.emit("status", "error");
      console.log("📍 ====== END LOCATION UPDATE (ERROR) ======");
      return false;
    }
  }
}

// Export singleton instance
export default GlobalCartSyncService.getInstance();
